import {
	ChangeDetectionStrategy,
	Component,
	Inject,
	OnInit,
} from '@angular/core';
import { BaseComponent } from '@core/base.component';
import { Logger } from '@core/logger';
import { clientFormattedName, clientLegalName } from '@core/util/client';
import {
	BaseClient,
	BusinessClient,
	Client,
	PersonClient,
} from '@models/clients';
import { MatterClientType, MatterFileType } from '@models/matter';
import { ClientSearchService } from '@services/algolia/client-search.service';
import { BusinessState } from '@state/business';
import { MatterDispatch } from '@state/matter';
import { SelectedMatter } from '@state/matter/matter.selectors';
import { MatterClientDispatch } from '@state/matter_clients/matterClients.dispatch';
import {
	ApsConflictResolutionRequest,
	LeapApiService,
} from 'src/app/shared/api-repos/leap/leap-api.service';
import { DRAWER_DATA } from 'src/app/ui/drawer/drawer-data.token';
import { DrawerService } from 'src/app/ui/drawer/drawer.service';
import { Store, Actions } from '@ngxs/store';
import { deleteField } from '@angular/fire/firestore';
import { createId } from '@core/util/file';
import { firstValueFrom } from 'rxjs';

interface VM {
	client: Client;
	similarClients: Client[];
	selectedClient: Client;
	matterClientType: MatterClientType;
	fileType: MatterFileType;
	fetchingSimilarContacts: boolean;
}

const defaultVM: VM = {
	client: null,
	similarClients: [],
	selectedClient: null,
	matterClientType: null,
	fileType: null,
	fetchingSimilarContacts: true,
};

@Component({
	selector: 'app-review-similar-party',
	templateUrl: './review-similar-party.component.html',
	styleUrls: ['./review-similar-party.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: false,
})
export class ReviewSimilarPartyComponent
	extends BaseComponent<VM>
	implements OnInit
{
	public isLeapMatter = this.store.selectSnapshot(SelectedMatter.isLeapMatter);
	public nameFormattingSettings = this.store.selectSnapshot(
		BusinessState.getNameFormattingSettings,
	);
	public matterId = this.store.selectSnapshot(SelectedMatter.id);

	constructor(
		private drawerService: DrawerService,
		@Inject(DRAWER_DATA)
		private data: {
			client: Client;
			matterClientType: MatterClientType;
			fileType: MatterFileType;
		},
		private matterClientsDispatch: MatterClientDispatch,
		private leapApiService: LeapApiService,
		private clientSearchService: ClientSearchService,
		private matterDispatch: MatterDispatch,
		private store: Store,
		private actions$: Actions,
	) {
		super(defaultVM);

		this.VM = {
			client: data.client,
			matterClientType: data.matterClientType,
			fileType: data.fileType,
		};
	}

	async ngOnInit() {
		const results = [];

		try {
			this.VM = {
				fetchingSimilarContacts: true,
			};

			const nameToSearch = clientFormattedName({
				...this.VM.client,
				middleNames: '',
			} as PersonClient);

			if (this.isLeapMatter) {
				// Get similar clients from Leap when in Leap
				const leapSearchResults = (
					(await firstValueFrom(
						this.leapApiService.searchCardsAps(nameToSearch),
					)) as Array<PersonClient | BusinessClient>
				).filter((x) => x.id !== this.VM.client.id);

				results.push(...leapSearchResults);
			} else {
				// Search Algolia when not in Leap
				const algoliaSearchResults = (
					(await this.clientSearchService.search(
						nameToSearch,
					)) as Array<BaseClient>
				).filter((x) => x.id !== this.VM.client.id);

				results.push(...algoliaSearchResults);
			}

			this.VM = {
				similarClients: results,
			};
		} catch (e) {
			Logger.error(`Error searching for similar clients: ${e}`);
		} finally {
			this.VM = {
				fetchingSimilarContacts: false,
			};
		}
	}

	cancel = (): void => {
		this.drawerService.closeDrawer();
	};

	confirm = async (): Promise<void> => {
		try {
			const isClient =
				this.getClientTypeForCurrFileType() === this.VM.matterClientType;
			// When in Leap, resolve APS client conflicts through the api
			if (
				this.isLeapMatter &&
				this.VM.selectedClient.id !== this.VM.client.id
			) {
				this.drawerService.closeDrawer();

				const request = this.getApsConflictResolutionRequest();
				const closerClientId = await firstValueFrom(
					this.leapApiService.resolveApsConflicts(request),
				);

				this.matterDispatch.OverwriteFields(
					`suggestedClients.${this.VM.client.id}`,
					deleteField(),
				);

				if (
					closerClientId &&
					this.VM.matterClientType === MatterClientType.BUYER
				) {
					this.matterClientsDispatch.SetOfferorTypeAndName(
						closerClientId,
						clientLegalName(
							this.VM.selectedClient,
							this.nameFormattingSettings,
						),
					);
				}

				return;
			}

			this.matterDispatch.OverwriteFields(
				`suggestedClients.${this.VM.client.id}`,
				deleteField(),
			);

			if (this.VM.selectedClient.id === this.VM.client.id) {
				// Handle new client creation in Leap and SAC here
				this.matterClientsDispatch.CreateAndAddClientToMatter({
					client: this.VM.client,
					matterClientRelationship: {
						id: createId(),
						clientTypes: [this.VM.matterClientType],
						primaryContact: false,
						client: isClient,
					},
				});
			} else {
				console.log('adding client');
				this.matterClientsDispatch.AddClient({
					id: this.VM.selectedClient.id,
					clientTypes: [this.VM.matterClientType],
					primaryContact: false,
					client: isClient,
				});
			}

			this.drawerService.closeDrawer();
			return;
		} catch (err) {
			Logger.error(`Error resolving similar party client: ${err}`);
		}
	};

	getClientTypeForCurrFileType() {
		return {
			[MatterFileType.PURCHASE]: MatterClientType.BUYER,
			[MatterFileType.SALE]: MatterClientType.SELLER,
			[MatterFileType.REFINANCE]: MatterClientType.BORROWER,
		}[this.VM.fileType];
	}

	getApsConflictResolutionRequest(): ApsConflictResolutionRequest {
		const clientToResolve =
			this.VM.selectedClient.id === this.VM.client.id
				? this.VM.client
				: this.VM.similarClients.find(
						(x) => x.id === this.VM.selectedClient.id,
					);

		const request: ApsConflictResolutionRequest = {
			map: {
				suggestionClientId: this.VM.client.id,
				selectedClientId: clientToResolve.leap ? undefined : clientToResolve.id,
				selectedLeap: clientToResolve.leap ? clientToResolve.leap : undefined,
			},
			matterId: this.matterId,
		};

		return request;
	}
}
